import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const steps = [
  {
    number: "01",
    title: "Hold FN",
    desc: "Press and hold the FN key on your keyboard. ClawdVoice activates instantly — no setup, no login screen.",
    key: "FN",
  },
  {
    number: "02",
    title: "Speak your command",
    desc: "Say what you want naturally. 'Open Slack', 'Draft email to John', 'Start a new note'. ClawdVoice understands context.",
    key: "...",
  },
  {
    number: "03",
    title: "Done",
    desc: "Release the key. Your command is executed immediately. The app opens, the text is typed, the email is drafted.",
    key: "✓",
  },
];

function Step({ step, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      data-testid={`step-${index}`}
      className="flex gap-8 items-start"
    >
      {/* Step number + line */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="w-12 h-12 bg-zinc-900 text-white rounded-sm flex items-center justify-center">
          <span className="font-mono text-sm font-bold">{step.number}</span>
        </div>
        {index < steps.length - 1 && (
          <div className="w-px h-20 bg-zinc-200 mt-3" />
        )}
      </div>

      {/* Content */}
      <div className="pb-16">
        <div className="flex items-center gap-3 mb-3">
          <h3
            style={{ fontFamily: "Manrope, sans-serif" }}
            className="text-xl md:text-2xl font-semibold text-zinc-900"
          >
            {step.title}
          </h3>
          {step.key !== "..." && step.key !== "✓" && (
            <span className="fn-key text-xs px-2 py-1 inline-flex">{step.key}</span>
          )}
          {step.key === "✓" && (
            <span className="inline-flex items-center justify-center w-7 h-7 bg-green-500 text-white text-xs rounded-sm font-bold">
              ✓
            </span>
          )}
        </div>
        <p className="text-zinc-500 leading-relaxed max-w-md">{step.desc}</p>
      </div>
    </motion.div>
  );
}

export default function HowItWorks() {
  const titleRef = useRef(null);
  const titleInView = useInView(titleRef, { once: true });

  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="py-32 bg-white border-t border-zinc-100"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
          {/* Left: Title */}
          <div ref={titleRef} className="lg:self-start lg:top-32 lg:sticky">
            <motion.p
              initial={{ opacity: 0 }}
              animate={titleInView ? { opacity: 1 } : {}}
              className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-4"
            >
              How it works
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              animate={titleInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              style={{ fontFamily: "Manrope, sans-serif" }}
              className="text-3xl md:text-5xl font-semibold tracking-tight text-zinc-900 mb-6"
            >
              Three steps.
              <br />
              Zero friction.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={titleInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2 }}
              className="text-zinc-500 text-base leading-relaxed max-w-sm"
            >
              ClawdVoice was designed to get out of your way. No menus, no
              setup screens, no learning curve.
            </motion.p>

            {/* Visual: keyboard row */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={titleInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="mt-10 flex items-center gap-2"
            >
              {["ctrl", "alt", "fn", "space"].map((k) => (
                <div
                  key={k}
                  className={`rounded-sm border border-zinc-200 bg-white shadow-sm flex items-center justify-center font-mono text-xs font-semibold text-zinc-600 ${
                    k === "fn"
                      ? "bg-zinc-900 text-white border-zinc-900 px-4 py-3 text-sm"
                      : k === "space"
                      ? "px-10 py-3"
                      : "px-3 py-3"
                  }`}
                >
                  {k}
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Steps */}
          <div className="pt-2">
            {steps.map((step, i) => (
              <Step key={i} step={step} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
